import { useEffect, useRef, useState } from "react";
import { api, getNarrationVoice } from "@/lib/api";
import { setAudioPlaying } from "@/lib/audioLock";

const SPOKEN_KEY_PREFIX = "smartguru_voice_spoken_";

/** Speaks a canned page-intro line once via /api/voice/tts.
 * `once=true` persists across visits (localStorage) - use for "first time ever" prompts.
 * `once=false` speaks again every time `key` changes (e.g. new concept/section entered). */
export function useVoicePrompt(text, key, { enabled = true, once = false } = {}) {
  const [blocked, setBlocked] = useState(false);
  const firedKeyRef = useRef(null);
  const pendingBase64Ref = useRef(null);

  const attemptPlay = (base64) => {
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      setAudioPlaying(true);
      audio.onended = () => setAudioPlaying(false);
      audio.onerror = () => setAudioPlaying(false);
      audio.play().catch(() => {
        setAudioPlaying(false);
        pendingBase64Ref.current = base64;
        setBlocked(true);
      });
    } catch {
      setAudioPlaying(false);
    }
  };

  useEffect(() => {
    if (!enabled || !text || !key || firedKeyRef.current === key) return;
    if (once && localStorage.getItem(SPOKEN_KEY_PREFIX + key)) {
      firedKeyRef.current = key;
      return;
    }
    firedKeyRef.current = key;
    setAudioPlaying(true);
    api.post("/voice/tts", { text, narration_voice: getNarrationVoice() }).then(({ data }) => {
      if (once) localStorage.setItem(SPOKEN_KEY_PREFIX + key, "1");
      attemptPlay(data.audio_base64);
    }).catch(() => { setAudioPlaying(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, once, text]);

  const retry = () => {
    setBlocked(false);
    if (pendingBase64Ref.current) attemptPlay(pendingBase64Ref.current);
  };

  return { blocked, retry };
}
